import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, Flex, Tooltip, Typography } from 'antd';
import moment from 'moment';
import { Link } from 'react-router-dom';

import {
  LEAD_CARD_PATH,
  U_ACTION,
  U_CLIENT_RECORDS_COST,
  U_INSTAGRAM,
  U_IS_CLIENT_REPLIED,
  U_INTEGRATIONS,
  U_IS_CLIENT_RECORDED,
  U_KEY,
  U_NAME,
  U_PHONE,
  U_STATUS,
  U_TIME,
  LOYALTY_PROGRAM_SENDOUTS,
} from '@app/constants';
import { STATUS_FILTER_DATA } from '@app/pages/Sendouts/SendoutReceivers/constants/filtersData';
import type { NSendoutsApi } from '@app/services/rtkQuery/Sendouts/types';
import { SocialIntegrationIcon } from '@app/shared/ui/SocialIntegrationIcon';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { useNavigate } from 'react-router-dom';

const { Text } = Typography;

export enum SendoutReceiversStatuses {
  QUEUED = 'queued',
  SENT = 'sent',
  ERROR = 'error',
}

const getStatusColor = (status?: string | null) => {
  switch (status) {
    case SendoutReceiversStatuses.SENT:
      return 'rgb(45, 165, 64)';
    case SendoutReceiversStatuses.QUEUED:
      return 'rgb(2, 117, 216)';
    case SendoutReceiversStatuses.ERROR:
      return 'rgb(225, 79, 79)';
    default:
      return 'rgba(0, 0, 0, 0.88)';
  }
};

const notEditableValuesTip =
  'Значение для этой колонки заполняется программой, ' +
  'оно не может быть изменено';

export const adaptDataToTable = (
  data: NSendoutsApi.GetSendoutClientsResponse['Data'],
  navigate: ReturnType<typeof useNavigate>
) => {
  return data?.map((sendout) => {
    const {
      Id,
      Name,
      Phone1,
      Status,
      ErrorMessage,
      Integration,
      ChannelId,
      TimeSent,
      LeadId,
      IsClientReplied,
      IsClientRecorded,
      ClientRecordsCost,
    } = sendout;

    return {
      [U_KEY]: String(Id),
      [U_NAME]: (
        <>
          {Name ? (
            <Link
              to={`${LEAD_CARD_PATH}?leadId=${LeadId}`}
              data-testid="client-name"
            >
              {Name}
            </Link>
          ) : (
            ''
          )}
        </>
      ),
      [U_PHONE]: (
        <Text data-testid="client-phone">{Phone1 ? `+${Phone1}` : ''}</Text>
      ),
      [U_INSTAGRAM]: (
        <Text data-testid="client-instagram-account">
          {sendout.UsernameInstagram || '-'}
        </Text>
      ),
      VK: (
        <Text data-testid="client-vk-account">
          {sendout?.UsernameVk || '-'}
        </Text>
      ),
      TelegramBot: (
        <Text data-testid="client-telegram-bot-account">
          {sendout?.UsernameTelegramBot || '-'}
        </Text>
      ),
      Telegram: (
        <Text data-testid="client-telegram-account">
          {sendout?.UsernameTelegram || '-'}
        </Text>
      ),
      Max: (
        <Text data-testid="client-max-account">
          {sendout?.UsernameMax || '-'}
        </Text>
      ),
      [LOYALTY_PROGRAM_SENDOUTS]: (
        <Text data-testid="client-loyalty-program-account">
          {sendout?.UsernameLoyalty || '-'}
        </Text>
      ),
      [U_INTEGRATIONS]: (
        <Flex gap={8} align="center">
          <SocialIntegrationIcon type={ChannelId} size="lg" />
          <div data-testid="integration-value">{Integration}</div>
        </Flex>
      ),
      [U_STATUS]: (
        <Text
          style={{ color: getStatusColor(Status) }}
          data-testid="sendout-status"
        >
          {ErrorMessage ? (
            <>
              {' '}
              <Tooltip title={ErrorMessage}>
                <FontAwesomeIcon
                  color={getStatusColor(SendoutReceiversStatuses.ERROR)}
                  icon={'fa-solid fa-circle-exclamation' as IconProp}
                />
              </Tooltip>
            </>
          ) : (
            ''
          )}{' '}
          {STATUS_FILTER_DATA.find((item) => item.value === Status)?.label ||
            ''}
        </Text>
      ),
      [U_TIME]: (
        <div data-testid="send-time-value">
          {TimeSent ? moment(TimeSent).format('DD.MM.YYYY HH:mm') : ''}
        </div>
      ),
      [U_IS_CLIENT_REPLIED]: (
        <Flex justify="center">
          <Tooltip title={notEditableValuesTip}>
            <Checkbox
              checked={IsClientReplied}
              disabled
              data-testid={
                IsClientReplied ? 'answer-exists' : 'answer-not-exists'
              }
            />
          </Tooltip>
        </Flex>
      ),
      [U_IS_CLIENT_RECORDED]: (
        <Flex justify="center">
          <Tooltip title={notEditableValuesTip}>
            <Checkbox
              checked={IsClientRecorded}
              disabled
              data-testid={
                IsClientRecorded ? 'record-exists' : 'record-not-exists'
              }
            />
          </Tooltip>
        </Flex>
      ),
      [U_CLIENT_RECORDS_COST]: (
        <Flex justify="center">
          <span data-testid="amount-value">{ClientRecordsCost}</span>
        </Flex>
      ),
      [U_ACTION]: (
        <div>
          <Tooltip title="Перейти в карточку лида">
            <Button
              size="small"
              onClick={() => {
                navigate(`${LEAD_CARD_PATH}?leadId=${LeadId}`);
              }}
              data-testid="client-card-link"
            >
              <FontAwesomeIcon icon={'fa-solid fa-eye' as IconProp} />
            </Button>
          </Tooltip>
        </div>
      ),
    };
  });
};
